import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Droplets, Fence, Home as HomeIcon, Lock, LogOut, MapPin, Menu, Phone, Sparkles, Star, Trash2, Waves, X } from "lucide-react";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = window.location.hostname === "localhost" ? `${BACKEND_URL}/api` : "/api";
const BUSINESS_PHONE_DISPLAY = "407-319-3313";
const BUSINESS_PHONE_TEL = "tel:+14073193313";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_narcoossee-wash/artifacts/3whi8oww_Nona%20Pressure%20Washing%20logo.png";

const HEADER_LOGO_URL =
  "https://customer-assets.emergentagent.com/job_narcoossee-wash/artifacts/v414nf8h_Worded%20Logo.png";

const services = [
  {
    title: "Fence Cleaning",
    text: "Brighten vinyl and wood fences by washing away algae, mildew, and grime.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/c114bfcf-96ad-45a1-80e5-ff457680a6bf/images/4df07d25ea560e8626df802aab8eba55041f0615ee561d3c112a1c65d7700d4d.png",
    icon: Fence,
  },
  {
    title: "Porch Cleaning",
    text: "Make entryways, patios, and outdoor hangout spots feel fresh again.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/c114bfcf-96ad-45a1-80e5-ff457680a6bf/images/8454cfec539715d1265fcc459f4b1cc830027c0a7a647f02a0df38d08d0325c4.png",
    icon: HomeIcon,
  },
  {
    title: "Driveway Cleaning",
    text: "Blast stains, tire marks, and built-up dirt from concrete and paver driveways for serious curb appeal.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/c114bfcf-96ad-45a1-80e5-ff457680a6bf/images/419562dd1f5da81318ff41ec73e0fcb4e37ee11d16094b72bc39e732233e9b70.png",
    icon: Droplets,
  },
  {
    title: "Sidewalks",
    text: "Clean sidewalks, walkways, and paver paths that look safer, brighter, and ready for guests.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/c114bfcf-96ad-45a1-80e5-ff457680a6bf/images/8414c5d982a0ad9901929e86f1c99fc355a6b2ace2b5c7020c52d5f3897f4630.png",
    icon: Waves,
  },
];

const beforeAfterPhotos = [
  {
    beforeImage:
      "https://customer-assets-agu9un31.emergentagent.net/job_narcoossee-wash/artifacts/nq06grwc_House%20siding%20before.webp",
    afterImage:
      "https://customer-assets-agu9un31.emergentagent.net/job_narcoossee-wash/artifacts/g2esitdd_House%20siding%20after.webp",
    alt: "House siding pressure washing before and after cleaning in Narcoossee and Lake Nona",
    beforePosition: "center center",
    afterPosition: "center center",
  },
  {
    beforeImage:
      "https://customer-assets.emergentagent.com/job_narcoossee-wash/artifacts/kq51a6k7_Flower%20Before.jpg",
    afterImage:
      "https://customer-assets.emergentagent.com/job_narcoossee-wash/artifacts/0jr5mrl2_Flower%20After.jpg",
    alt: "Sidewalk pressure washing before and after cleaning near flower beds in Narcoossee",
    beforePosition: "center center",
    afterPosition: "center center",
  },
  {
    beforeImage:
      "https://customer-assets.emergentagent.com/job_narcoossee-wash/artifacts/pz7ybk76_Zarela%20Sidewalk%20Before.jpg",
    afterImage:
      "https://customer-assets.emergentagent.com/job_narcoossee-wash/artifacts/uexj0rot_Zarela%20Sidewalk%20After.jpeg",
    alt: "Residential sidewalk pressure washing before and after cleaning in Lake Nona",
    beforePosition: "50% top",
    afterPosition: "50% top",
  },
  {
    beforeImage:
      "https://customer-assets.emergentagent.com/job_narcoossee-wash/artifacts/hg7nqo6r_Pool%20Before.jpeg",
    afterImage:
      "https://customer-assets.emergentagent.com/job_narcoossee-wash/artifacts/ca2cpa15_Pool%20after.jpeg",
    alt: "Pool patio and screen enclosure pressure washing before and after cleaning",
  },
];

const BeforeAfterSlider = ({ photo, index }) => {
  const [position, setPosition] = useState(50);
  const sliderPosition = Number(position);
  const beforeImage = photo.beforeImage || photo.image;
  const afterImage = photo.afterImage || photo.image;
  const hasSeparateImages = Boolean(photo.beforeImage && photo.afterImage);
  const fitClass = photo.fitMode === "contain" ? "fit-contain" : "fit-cover";

  return (
    <article className={`before-after-card slider-card ${hasSeparateImages ? "real-photo-card" : ""}`} data-testid={`before-after-card-${index}`}>
      <div className="comparison-slider" data-testid={`before-after-slider-${index}`} style={{ "--slider-position": `${position}%` }}>
        <div
          className={`comparison-photo before-photo ${hasSeparateImages ? `full-photo ${fitClass}` : "split-photo"}`}
          data-testid={`before-photo-${index}`}
          style={{ backgroundImage: `url(${beforeImage})`, backgroundPosition: photo.beforePosition, backgroundSize: photo.imageScale }}
          aria-hidden="true"
        />
        <div
          className={`comparison-photo after-photo ${hasSeparateImages ? `full-photo ${fitClass}` : "split-photo"}`}
          data-testid={`after-photo-${index}`}
          style={{ backgroundImage: `url(${afterImage})`, backgroundPosition: photo.afterPosition, backgroundSize: photo.imageScale }}
          aria-hidden="true"
        />
        <img className="sr-only" src={afterImage} alt={photo.alt} data-testid={`before-after-image-${index}`} />
        {sliderPosition > 22 && (
          <div className="slider-before-label" data-testid={`before-label-${index}`}>
            Before
          </div>
        )}
        {sliderPosition < 78 && (
          <div className="slider-after-label" data-testid={`after-label-${index}`}>
            After
          </div>
        )}
        <div className="slider-divider" data-testid={`slider-divider-${index}`} aria-hidden="true">
          <span />
        </div>
        <input
          className="comparison-range"
          data-testid={`before-after-range-${index}`}
          type="range"
          min="0"
          max="100"
          value={position}
          aria-label="Slide to compare before and after photo"
          onInput={(event) => setPosition(event.target.value)}
          onChange={(event) => setPosition(event.target.value)}
        />
      </div>
    </article>
  );
};

const testimonials = [
  {
    name: "Rob F.",
    quote:
      "Narcoossee Pressure Washing did a fantastic job on our front porch pavers, sidewalk, and curbs. They were friendly, on time, and very professional... Highly Recommend!",
    service: "Porch Pavers, Sidewalk & Curbs",
  },
  {
    name: "Kim T.",
    quote:
      "My driveway and sidewalks have never been whiter than they are today. He did an excellent job cleaning all surfaces beautifully. I would highly recommend his services.",
    service: "Garage Apron and Sidewalk Cleaning",
  },
  {
    name: "Kelli",
    quote:
      "He arrived when he stated he would, explained the scope of work and was polite and professional! The work he did was exceptional and my sidewalks have never looked better! He is the only pressure washing business I would recommend or use in the future!",
    service: "Porch Pavers and Sidewalks",
  },
];

const faqs = [
  {
    question: "Do you provide pressure washing in Narcoossee and Lake Nona?",
    answer: "Yes. Narcoossee Pressure Washing serves homeowners in Narcoossee, Lake Nona, and nearby Orlando communities.",
  },
  {
    question: "What surfaces do you clean?",
    answer: "We clean driveways, sidewalks, brick pavers, fences, porches, patios, and other outdoor concrete or exterior surfaces.",
  },
  {
    question: "Can pressure washing remove algae, mildew, and dark stains?",
    answer: "Pressure washing can remove many common outdoor stains including algae, mildew, dirt, grime, tire marks, and surface buildup.",
  },
  {
    question: "How do I get a free pressure washing quote?",
    answer: "Fill out the quote form with your name, phone number, and address. We will follow up with you about your cleaning needs.",
  },
];

const scrollToQuote = () => {
  document.getElementById("quote-form")?.scrollIntoView({ behavior: "smooth", block: "center" });
};

const trackCallClick = () => {
  if (window.fbq) {
    window.fbq("track", "Contact", { content_name: "Call Me Button" });
  }
};

const formatLeadDate = (dateValue) => {
  if (!dateValue) return "Unknown date";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(dateValue));
};

const QuoteForm = () => {
  const [formData, setFormData] = useState({ name: "", phone: "", address: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const updateField = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const submitLead = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSuccessMessage("");

    try {
      const response = await axios.post(`${API}/leads`, formData);
      const message = response.data?.message || "Thanks! We'll text you soon about your free quote.";
      setSuccessMessage(message);
      toast.success(message);
      setFormData({ name: "", phone: "", address: "" });
    } catch (error) {
      const message = error.response?.data?.detail || "We couldn't send your request. Please check your connection and try again.";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form id="quote-form" data-testid="quote-form" className="quote-card" onSubmit={submitLead}>
      <div className="form-badge" data-testid="quote-form-badge">
        <Sparkles size={18} aria-hidden="true" /> Fast Free Quote
      </div>
      <h2 data-testid="quote-form-title">Ready for a cleaner property?</h2>
      <p data-testid="quote-form-description">
        Drop your details below and Narcoossee Pressure Washing will text you back.
      </p>

      <label htmlFor="name" data-testid="quote-name-label">
        Name
        <input
          id="name"
          data-testid="quote-name-input"
          type="text"
          value={formData.name}
          onChange={(event) => updateField("name", event.target.value)}
          placeholder="Your name"
          required
          minLength="2"
        />
      </label>

      <label htmlFor="phone" data-testid="quote-phone-label">
        Phone
        <input
          id="phone"
          data-testid="quote-phone-input"
          type="tel"
          value={formData.phone}
          onChange={(event) => updateField("phone", event.target.value)}
          placeholder="(407) 555-0123"
          required
          minLength="7"
        />
      </label>

      <label htmlFor="address" data-testid="quote-address-label">
        Address
        <input
          id="address"
          data-testid="quote-address-input"
          type="text"
          value={formData.address}
          onChange={(event) => updateField("address", event.target.value)}
          placeholder="Street address"
          required
          minLength="5"
        />
      </label>

      <button data-testid="quote-submit-button" type="submit" disabled={isSubmitting}>
        {isSubmitting ? "Sending..." : "Get a Free Quote"}
      </button>

      {successMessage && (
        <div className="success-pop" data-testid="quote-success-message" role="status">
          {successMessage}
        </div>
      )}
    </form>
  );
};

const Home = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const closeMenu = () => setIsMenuOpen(false);

  return (
    <div className="site-shell" data-testid="homepage">
      <nav className="top-nav" data-testid="site-header">
        <a href="#top" className="brand-mark" data-testid="header-logo-link" aria-label="Narcoossee Pressure Washing home">
          <img src={HEADER_LOGO_URL} alt="Narcoossee Pressure Washing" data-testid="header-logo-image" />
          <span data-testid="header-brand-name" className="sr-only">Narcoossee Pressure Washing</span>
        </a>
        <button
          className="mobile-menu-button"
          data-testid="mobile-menu-button"
          type="button"
          aria-expanded={isMenuOpen}
          aria-controls="header-menu"
          onClick={() => setIsMenuOpen((current) => !current)}
        >
          {isMenuOpen ? <X size={21} aria-hidden="true" /> : <Menu size={21} aria-hidden="true" />}
          Menu
        </button>
        <div id="header-menu" className={`nav-menu ${isMenuOpen ? "is-open" : ""}`} data-testid="header-menu">
          <div className="nav-links" data-testid="header-nav-links" aria-label="Page sections">
            <a href="#before-after" data-testid="header-results-link" onClick={closeMenu}>Results</a>
            <a href="#testimonials" data-testid="header-reviews-link" onClick={closeMenu}>Reviews</a>
            <a href="#services" data-testid="header-services-link" onClick={closeMenu}>Services</a>
            <a href="#quote-form" data-testid="header-quote-link" onClick={closeMenu}>Quote</a>
          </div>
          <button className="nav-cta" data-testid="header-quote-button" onClick={() => { closeMenu(); scrollToQuote(); }} type="button">
            Get a Free Quote
          </button>
        </div>
      </nav>

      <main id="top">
        <section className="hero-section" data-testid="hero-section">
          <div className="hero-copy" data-testid="hero-copy">
            <div className="service-area-pill" data-testid="service-area-pill">
              <MapPin size={18} aria-hidden="true" /> Serving Narcoossee & Lake Nona
            </div>
            <h1 data-testid="hero-title">Blast the Dirt Away!</h1>
            <p data-testid="hero-subtitle">
              Bold, friendly pressure washing for driveways, sidewalks, fences, and porches — with a clean finish that pops.
            </p>
            <div className="hero-actions" data-testid="hero-actions">
              <button className="primary-splash-button" data-testid="hero-primary-quote-button" onClick={scrollToQuote} type="button">
                Get a Free Quote
              </button>
              <a className="phone-chip" data-testid="hero-call-button" href={BUSINESS_PHONE_TEL} onClick={trackCallClick}>
                <Phone size={18} aria-hidden="true" /> Call Me: {BUSINESS_PHONE_DISPLAY}
              </a>
            </div>
          </div>

          <div className="hero-art-card" data-testid="hero-art-card">
            <img src={LOGO_URL} alt="Narcoossee Pressure Washing" data-testid="hero-art-image" />
          </div>
        </section>

        <section id="before-after" className="before-after-section" data-testid="before-after-section">
          <div className="before-after-heading" data-testid="before-after-heading-block">
            <span data-testid="before-after-eyebrow">Before & after</span>
            <h2 data-testid="before-after-title">See the clean difference fast.</h2>
            <p data-testid="before-after-description">
              From dingy concrete to bright curb appeal, pressure washing can make outdoor surfaces look new again.
            </p>
          </div>

          <div className="before-after-grid" data-testid="before-after-grid">
            {beforeAfterPhotos.map((photo, index) => (
              <BeforeAfterSlider photo={photo} index={index} key={photo.image || photo.beforeImage} />
            ))}
          </div>
        </section>

        <section className="quote-section" data-testid="quote-section">
          <QuoteForm />
        </section>

        <section id="testimonials" className="testimonials-section" data-testid="testimonials-section">
          <div className="testimonials-heading" data-testid="testimonials-heading-block">
            <span data-testid="testimonials-eyebrow">Happy customers</span>
            <h2 data-testid="testimonials-title">Clean results people notice.</h2>
            <p data-testid="testimonials-description">
              A cleaner driveway, porch, fence, or sidewalk can change the first impression of the whole property.
            </p>
          </div>

          <div className="testimonials-grid" data-testid="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <article className="testimonial-card" data-testid={`testimonial-card-${index}`} key={testimonial.name}>
                <div className="testimonial-stars" data-testid={`testimonial-stars-${index}`} aria-label="Five star review">
                  {[0, 1, 2, 3, 4].map((star) => (
                    <Star key={star} size={20} fill="currentColor" aria-hidden="true" />
                  ))}
                </div>
                <p className="testimonial-quote" data-testid={`testimonial-quote-${index}`}>“{testimonial.quote}”</p>
                <div className="testimonial-author" data-testid={`testimonial-author-${index}`}>
                  <strong data-testid={`testimonial-name-${index}`}>{testimonial.name}</strong>
                  <span data-testid={`testimonial-service-${index}`}>{testimonial.service}</span>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="trust-strip" data-testid="trust-strip">
          <div data-testid="trust-item-local"><strong>Local</strong><span>Narcoossee / Lake Nona</span></div>
          <div data-testid="trust-item-fast"><strong>Fast</strong><span>Quote follow-up by text</span></div>
          <div data-testid="trust-item-bright"><strong>Bright</strong><span>Fresh curb appeal</span></div>
        </section>

        <section id="services" className="services-section" data-testid="services-section">
          <div className="section-heading" data-testid="services-heading-block">
            <span data-testid="services-eyebrow">What we clean</span>
            <h2 data-testid="services-title">Pressure washing that makes your place shine.</h2>
          </div>

          <div className="services-grid" data-testid="services-grid">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <article className="service-card" data-testid={`service-card-${index}`} key={service.title}>
                  <div className="service-image-wrap" data-testid={`service-image-wrap-${index}`}>
                    <img src={service.image} alt={`${service.title} illustration`} data-testid={`service-image-${index}`} />
                  </div>
                  <div className="service-copy" data-testid={`service-copy-${index}`}>
                    <div className="service-icon" data-testid={`service-icon-${index}`}>
                      <Icon size={24} aria-hidden="true" />
                    </div>
                    <h3 data-testid={`service-title-${index}`}>{service.title}</h3>
                    <p data-testid={`service-description-${index}`}>{service.text}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section className="local-seo-section" data-testid="local-seo-section">
          <div className="local-seo-card" data-testid="local-seo-card">
            <span data-testid="local-seo-eyebrow">Local pressure washing</span>
            <h2 data-testid="local-seo-title">Pressure Washing in Narcoossee & Lake Nona</h2>
            <p data-testid="local-seo-description">
              Narcoossee Pressure Washing helps homeowners refresh driveways, sidewalks, porches, and fences with friendly local service. If your concrete or outdoor surfaces are looking dull, stained, or covered in Florida grime, request a free quote and bring back the curb appeal.
            </p>
          </div>
        </section>

        <section className="faq-section" data-testid="faq-section">
          <div className="faq-heading" data-testid="faq-heading-block">
            <span data-testid="faq-eyebrow">Quick answers</span>
            <h2 data-testid="faq-title">Pressure washing FAQs</h2>
          </div>

          <div className="faq-grid" data-testid="faq-grid">
            {faqs.map((faq, index) => (
              <article className="faq-card" data-testid={`faq-card-${index}`} key={faq.question}>
                <h3 data-testid={`faq-question-${index}`}>{faq.question}</h3>
                <p data-testid={`faq-answer-${index}`}>{faq.answer}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="final-cta" data-testid="final-cta-section">
          <div data-testid="final-cta-copy">
            <span data-testid="final-cta-eyebrow">No grime left behind</span>
            <h2 data-testid="final-cta-title">Let’s get your outdoor surfaces looking sharp.</h2>
          </div>
          <div className="final-cta-actions" data-testid="final-cta-actions">
            <a className="call-cta final-call-cta" data-testid="final-call-button" href={BUSINESS_PHONE_TEL} onClick={trackCallClick}>
              <Phone size={18} aria-hidden="true" /> Call Me
            </a>
            <button data-testid="final-quote-button" onClick={scrollToQuote} type="button">
              Get a Free Quote
            </button>
          </div>
        </section>
      </main>

      <footer className="site-footer" data-testid="site-footer">
        <p data-testid="footer-copy">© 2026 Narcoossee Pressure Washing. Proudly serving Narcoossee & Lake Nona. Call {BUSINESS_PHONE_DISPLAY}.</p>
      </footer>
    </div>
  );
};

const AdminDashboard = () => {
  const [password, setPassword] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [deletingLeadId, setDeletingLeadId] = useState("");
  const [error, setError] = useState("");

  const loadLeads = async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await axios.get(`${API}/admin/leads`, { withCredentials: true });
      setLeads(response.data || []);
    } catch (requestError) {
      const message = requestError.response?.data?.detail || "Unable to load leads. Please log in again.";
      setError(message);
      if (requestError.response?.status === 401) {
        setIsAuthenticated(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      await axios.post(`${API}/auth/login`, { password }, { withCredentials: true });
      setIsAuthenticated(true);
      setPassword("");
      toast.success("Admin login successful.");
      await loadLeads();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Incorrect password.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setIsAuthenticated(false);
    setLeads([]);
    setPassword("");
    toast.success("Logged out.");
  };

  const handleDeleteLead = async (lead) => {
    const shouldDelete = window.confirm(`Delete inquiry from ${lead.name}? This cannot be undone.`);
    if (!shouldDelete) return;

    setDeletingLeadId(lead.id);
    setError("");

    try {
      await axios.delete(`${API}/admin/leads/${lead.id}`, { withCredentials: true });
      setLeads((currentLeads) => currentLeads.filter((item) => item.id !== lead.id));
      toast.success("Inquiry deleted.");
    } catch (requestError) {
      const message = requestError.response?.data?.detail || "Unable to delete this inquiry.";
      setError(message);
      toast.error(message);
    } finally {
      setDeletingLeadId("");
    }
  };

  useEffect(() => {
    const checkSession = async () => {
      try {
        await axios.get(`${API}/auth/me`, { withCredentials: true });
        setIsAuthenticated(true);
        await loadLeads();
      } catch {
        setIsAuthenticated(false);
      } finally {
        setIsCheckingSession(false);
      }
    };

    checkSession();
  }, []);

  if (isCheckingSession) {
    return (
      <div className="admin-shell" data-testid="admin-session-loading">
        <div className="admin-login-card" data-testid="admin-loading-card">
          <div className="admin-lock-icon" data-testid="admin-loading-icon"><Lock size={28} aria-hidden="true" /></div>
          <h1 data-testid="admin-loading-title">Checking Access</h1>
          <p data-testid="admin-loading-description">Loading the owner dashboard...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="admin-shell" data-testid="admin-login-page">
        <form className="admin-login-card" data-testid="admin-login-form" onSubmit={handleLogin}>
          <div className="admin-lock-icon" data-testid="admin-login-icon"><Lock size={28} aria-hidden="true" /></div>
          <h1 data-testid="admin-login-title">Owner Dashboard</h1>
          <p data-testid="admin-login-description">Enter the owner password to view quote requests.</p>
          <label htmlFor="admin-password" data-testid="admin-password-label">
            Password
            <input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Owner password"
              required
            />
          </label>
          <button data-testid="admin-login-button" type="submit" disabled={isLoading}>
            {isLoading ? "Checking..." : "View Leads"}
          </button>
          {error && <div className="admin-error" data-testid="admin-login-error" role="alert">{error}</div>}
          <a className="admin-back-link" data-testid="admin-back-home-link" href="/">Back to website</a>
        </form>
      </div>
    );
  }

  return (
    <div className="admin-shell" data-testid="admin-dashboard-page">
      <section className="admin-dashboard" data-testid="admin-dashboard">
        <div className="admin-dashboard-header" data-testid="admin-dashboard-header">
          <div>
            <span data-testid="admin-dashboard-eyebrow">Owner-only</span>
            <h1 data-testid="admin-dashboard-title">Quote Requests</h1>
            <p data-testid="admin-dashboard-description">View saved quote requests and delete old inquiries when you no longer need them.</p>
          </div>
          <div className="admin-actions" data-testid="admin-dashboard-actions">
            <button data-testid="admin-refresh-button" type="button" onClick={() => loadLeads()} disabled={isLoading}>
              {isLoading ? "Loading..." : "Refresh"}
            </button>
            <button data-testid="admin-logout-button" type="button" onClick={handleLogout}>
              <LogOut size={18} aria-hidden="true" /> Logout
            </button>
          </div>
        </div>

        {error && <div className="admin-error" data-testid="admin-dashboard-error" role="alert">{error}</div>}

        <div className="admin-stats" data-testid="admin-lead-count-card">
          <strong data-testid="admin-lead-count">{leads.length}</strong>
          <span data-testid="admin-lead-count-label">Saved quote request{leads.length === 1 ? "" : "s"}</span>
        </div>

        <div className="leads-table-wrap" data-testid="admin-leads-table-wrap">
          {leads.length === 0 && !isLoading ? (
            <div className="admin-empty" data-testid="admin-empty-state">No quote requests yet.</div>
          ) : (
            <table className="leads-table" data-testid="admin-leads-table">
              <thead>
                <tr>
                  <th data-testid="admin-table-heading-name">Name</th>
                  <th data-testid="admin-table-heading-phone">Phone</th>
                  <th data-testid="admin-table-heading-address">Address</th>
                  <th data-testid="admin-table-heading-date">Date Submitted</th>
                  <th data-testid="admin-table-heading-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead, index) => (
                  <tr data-testid={`admin-lead-row-${index}`} key={lead.id}>
                    <td data-testid={`admin-lead-name-${index}`}>{lead.name}</td>
                    <td data-testid={`admin-lead-phone-${index}`}><a href={`tel:${lead.phone}`}>{lead.phone}</a></td>
                    <td data-testid={`admin-lead-address-${index}`}>{lead.address}</td>
                    <td data-testid={`admin-lead-date-${index}`}>{formatLeadDate(lead.created_at)}</td>
                    <td data-testid={`admin-lead-actions-${index}`}>
                      <button
                        className="delete-lead-button"
                        data-testid={`admin-delete-lead-button-${index}`}
                        type="button"
                        onClick={() => handleDeleteLead(lead)}
                        disabled={deletingLeadId === lead.id}
                      >
                        <Trash2 size={16} aria-hidden="true" />
                        {deletingLeadId === lead.id ? "Deleting..." : "Delete"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <Toaster richColors position="top-center" />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/admin" element={<AdminDashboard />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
